// src/app/api/wealthMethodApi.ts
import { baseApi } from "./baseApi";

/**
 * Local file reference (tooling will transform this to a real URL when sending).
 * As requested, we include the exact local path you uploaded so back-end or tooling can transform it.
 */
export const BOOK_FILE_URL = "/mnt/data/W.E.A.L.T.H METHOD.docx";

/* ----------------------
   Types
   ---------------------- */

export type WealthProduct = {
  id: string;
  name: string;
  price: string; // formatted price e.g. "₦45,000"
  priceNumeric: number; // numeric price e.g. 45000
  // optional: sourceFileUrl could be passed but your model doesn't require storing it
  sourceFileUrl?: string;
};

export type WealthFeature = {
  title: string;
  description?: string;
  value?: number;
};

export type WealthTotals = {
  featuresTotal: number;
  productPrice: number;
};

export type InitializeWealthMethodRequest = {
  firstName: string;
  email: string;
  phoneNo?: string;
  product: WealthProduct;
  features?: WealthFeature[];
  totals?: WealthTotals;
  metadata?: Record<string, any>;
};

export type InitializeWealthMethodResponse = {
  success: boolean;
  authorization_url?: string;
  reference?: string;
  orderId?: string;
  wealthMethodId?: string;
  message?: string;
};

export type VerifyWealthMethodResponse = {
  success: boolean;
  verified?: boolean;
  data?: any; // Paystack transaction object (kept generic)
  orderId?: string;
  message?: string;
};

export type GetWealthMethodResponse = {
  success: boolean;
  data?: any; // return the WealthMethod document (kept generic)
  message?: string;
};

/* ----------------------
   API injection
   ---------------------- */

export const wealthMethodApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    initializeWealthMethod: builder.mutation<
      InitializeWealthMethodResponse,
      InitializeWealthMethodRequest
    >({
      query: (payload) => ({
        url: "/wealth-method/initialize",
        method: "POST",
        body: payload,
      }),
    }),

    // Verify endpoint (GET /wealth-method/verify/:reference) - parity with payments.verify
    verifyWealthMethod: builder.query<VerifyWealthMethodResponse, string>({
      query: (reference) => ({
        url: `/wealth-method/verify/${encodeURIComponent(reference)}`,
        method: "GET",
      }),
    }),

    // Fetch a WealthMethod record by id
    getWealthMethod: builder.query<GetWealthMethodResponse, string>({
      query: (id) => ({
        url: `/wealth-method/${encodeURIComponent(id)}`,
        method: "GET",
      }),
      // Optionally provide tags to enable cache invalidation if you add them to your baseApi
    }),
  }),
  overrideExisting: false,
});

export const {
  useInitializeWealthMethodMutation,
  useVerifyWealthMethodQuery,
  useGetWealthMethodQuery,
} = wealthMethodApi;
